import { Reveal, Eyebrow } from "./Reveal";
import { SAGA_PROPERTIES } from "../data/content";
import { Clapperboard, Film, BookOpen } from "lucide-react";

const ICONS = [Clapperboard, Film, BookOpen];

export const Saga = () => (
    <section id="saga" data-testid="saga-section" className="relative overflow-hidden py-24 sm:py-36">
        <div className="pointer-events-none absolute right-[-10%] top-0 h-[480px] w-[480px] rounded-full bg-[#d6c68e]/10 blur-[160px]" />
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
            <Reveal className="mb-16 max-w-4xl">
                <Eyebrow>The Franchise Behind the Series</Eyebrow>
                <h2 className="deck-heading font-display text-5xl sm:text-6xl lg:text-7xl tracking-[0.12em]">
                    ONE TOWN. <span>EVERY FORMAT.</span>
                </h2>
                <p className="mt-6 text-base sm:text-lg leading-relaxed text-zinc-400">
                    Deadly Ambition is the anchor title of{" "}
                    <span className="text-white">The Osirica Saga</span> — a connected story world centred on the
                    fictional town of St. Bellmore, spanning television, film and literary properties. Each title
                    stands alone. Together, they build franchise value.
                </p>
            </Reveal>

            <div className="grid gap-4 lg:grid-cols-3">
                {SAGA_PROPERTIES.map((p, i) => {
                    const Icon = ICONS[i];
                    return (
                        <Reveal key={p.id} delay={i * 0.1}>
                            <article
                                data-testid={`saga-card-${p.id}`}
                                className={`group flex h-full flex-col border p-8 transition-all duration-500 ${
                                    i === 0
                                        ? "border-[#d6c68e]/50 bg-gradient-to-b from-[#d6c68e]/10 to-[#0b1220]"
                                        : "border-white/10 bg-[#0b1220] hover:border-white/25"
                                }`}
                            >
                                <div className="flex items-center justify-between">
                                    <Icon size={22} className={i === 0 ? "text-[#d6c68e]" : "text-zinc-500"} />
                                    <span className="font-mono text-[9px] tracking-[0.25em] text-zinc-500">{p.format}</span>
                                </div>
                                {p.covers && (
                                    <div className="mt-6 flex items-end justify-center gap-5">
                                        {p.covers.map((cv) => (
                                            <img
                                                key={cv.src}
                                                src={cv.src}
                                                alt={cv.alt}
                                                loading="lazy"
                                                className="h-44 w-auto drop-shadow-[0_18px_28px_rgba(0,0,0,0.6)] transition-transform duration-500 group-hover:-translate-y-2"
                                            />
                                        ))}
                                    </div>
                                )}
                                <h3 className="mt-6 font-display text-3xl tracking-[0.04em] text-white">{p.title.toUpperCase()}</h3>
                                <p className={`mt-1 font-mono text-[10px] tracking-[0.2em] ${i === 0 ? "text-[#d6c68e]" : "text-zinc-500"}`}>
                                    {p.status.toUpperCase()}
                                </p>
                                <p className="mt-4 text-sm leading-relaxed text-zinc-400">{p.text}</p>
                            </article>
                        </Reveal>
                    );
                })}
            </div>

            <Reveal delay={0.15} className="mt-16">
                <div className="grid gap-px border border-white/10 bg-white/10 lg:grid-cols-2">
                    <div data-testid="saga-campus-tour" className="bg-[#070c18]/70 p-8 sm:p-10">
                        <p className="font-mono text-[10px] tracking-[0.3em] text-[#d6c68e]">THE MARKETING MODEL</p>
                        <h3 className="mt-3 font-display text-3xl tracking-[0.04em] text-white">THE CAMPUS TOUR</h3>
                        <p className="mt-4 text-sm leading-relaxed text-zinc-400">
                            Stand in the room with the people who love it: cast and creatives in conversation with the
                            audience — Q&amp;As, in-character readings, campus stops treated as tour dates, Easter eggs
                            seeded for fans to find. Every stop funnels the fandom into the Substack and the shared
                            mythology. A world people can walk into beats a world they can only watch.
                        </p>
                    </div>
                    <div data-testid="saga-merchandise" className="bg-[#070c18]/70 p-8 sm:p-10">
                        <p className="font-mono text-[10px] tracking-[0.3em] text-[#d6c68e]">BEYOND THE SCREEN</p>
                        <h3 className="mt-3 font-display text-3xl tracking-[0.04em] text-white">MERCHANDISE &amp; DIGITAL</h3>
                        <p className="mt-4 text-sm leading-relaxed text-zinc-400">
                            Digital-first products built for dark-academia, dark-romance and true-crime fandoms —
                            journals, reading collections, artwork, stationery and mystery-inspired experiences. Each
                            product is a discovery path: social sharing, curiosity, and back to St. Bellmore.
                        </p>
                    </div>
                </div>
            </Reveal>
        </div>
    </section>
);
